import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, IconButton,
  Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, FormControlLabel, Switch, Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Public as GlobalIcon,
  Business as CompanyIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

// Default values for new templates
const DEFAULT_TEMPLATE = {
  name: '',
  description: '',
  analysisYears: 25,
  discountRate: 8.3,
  taxRate: 26.0,
  degradationRate: 0.5,
  escalationRates: {
    energy: 3.0,
    demand: 3.0,
    naturalGas: 2.5,
    labor: 2.5
  },
  is_global: false
};

const BudgetTemplatesPage = () => {
  const { currentUser, userProfile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_TEMPLATE });
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = userProfile?.is_super_admin;

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/budgets/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchTemplates();
    }
  }, [currentUser, fetchTemplates]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ ...template });
    } else {
      setEditingTemplate(null);
      setFormData({ ...DEFAULT_TEMPLATE });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
    setFormData({ ...DEFAULT_TEMPLATE });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      const url = editingTemplate
        ? `/api/site_dna/v1/budgets/templates/${editingTemplate.id}`
        : '/api/site_dna/v1/budgets/templates';
      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save template');
      }

      await fetchTemplates();
      handleCloseDialog();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/budgets/templates/${template.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete template');
      }

      await fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEscalationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      escalationRates: { ...prev.escalationRates, [field]: value }
    }));
  };

  return (
    <AdminLayout title="Budget Templates">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Manage financial assumption templates for proposals and scenarios.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchTemplates} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Template
          </Button>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="info">
          No budget templates found. Create your first template to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Analysis Years</TableCell>
                <TableCell align="right">Discount Rate</TableCell>
                <TableCell align="right">Tax Rate</TableCell>
                <TableCell align="right">Energy Escalation</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => {
                const canEdit = isSuperAdmin || (!template.is_global && template.company_id === userProfile?.company_id);
                return (
                  <TableRow key={template.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{template.name}</Typography>
                      {template.description && (
                        <Typography variant="caption" color="text.secondary">{template.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={template.is_global ? <GlobalIcon /> : <CompanyIcon />}
                        label={template.is_global ? 'Global' : 'Company'}
                        size="small"
                        sx={{
                          bgcolor: alpha(template.is_global ? vortxColors.teal : vortxColors.gold, 0.2),
                          color: template.is_global ? vortxColors.teal : vortxColors.gold
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{template.analysisYears} years</TableCell>
                    <TableCell align="right">{template.discountRate}%</TableCell>
                    <TableCell align="right">{template.taxRate}%</TableCell>
                    <TableCell align="right">{template.escalationRates?.energy || 3.0}%/yr</TableCell>
                    <TableCell align="center">
                      {canEdit && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenDialog(template)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(template)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Budget Template'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Template Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Description"
                fullWidth
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Grid>

            {/* Financial Parameters */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Financial Parameters
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Analysis Years"
                type="number"
                fullWidth
                value={formData.analysisYears}
                onChange={(e) => handleFormChange('analysisYears', parseInt(e.target.value) || 25)}
                inputProps={{ min: 1, max: 40 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Discount Rate (%)"
                type="number"
                fullWidth
                value={formData.discountRate}
                onChange={(e) => handleFormChange('discountRate', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 0, max: 30 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Tax Rate (%)"
                type="number"
                fullWidth
                value={formData.taxRate}
                onChange={(e) => handleFormChange('taxRate', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 0, max: 50 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Degradation Rate (%)"
                type="number"
                fullWidth
                value={formData.degradationRate}
                onChange={(e) => handleFormChange('degradationRate', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 0, max: 5 }}
              />
            </Grid>

            {/* Escalation Rates */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Annual Escalation Rates (%/year)
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Energy"
                type="number"
                fullWidth
                value={formData.escalationRates.energy}
                onChange={(e) => handleEscalationChange('energy', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 0, max: 10 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Demand"
                type="number"
                fullWidth
                value={formData.escalationRates.demand}
                onChange={(e) => handleEscalationChange('demand', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 0, max: 10 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Natural Gas"
                type="number"
                fullWidth
                value={formData.escalationRates.naturalGas}
                onChange={(e) => handleEscalationChange('naturalGas', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 0, max: 10 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Labor/O&M"
                type="number"
                fullWidth
                value={formData.escalationRates.labor}
                onChange={(e) => handleEscalationChange('labor', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 0, max: 10 }}
              />
            </Grid>

            {/* Global Toggle (Super Admin Only) */}
            {isSuperAdmin && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_global}
                      onChange={(e) => handleFormChange('is_global', e.target.checked)}
                    />
                  }
                  label="Make this a global template (available to all companies)"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.name}
          >
            {saving ? <CircularProgress size={20} /> : (editingTemplate ? 'Save Changes' : 'Create Template')}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default BudgetTemplatesPage;
